import { useEffect, useMemo, useState } from "react";
import {
  MdArrowForward,
  MdCheckCircle,
  MdLockOutline,
  MdSwapHoriz,
  MdTrendingUp,
  MdWorkspacePremium,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axios";
import SubscriptionPlansSkeleton from "../../components/subscription/SubscriptionPlansSkeleton";
import { useDashboard } from "../../contexts/DashboardContext";
import styles from "../../styles/SubscriptionPlans.module.css";
import {
  isAuthenticated,
  savePendingSubscriptionSelection,
} from "../../utils/auth";

const SUBSCRIPTION_PLANS_ENDPOINT = "/subscription-plans";
const SUBSCRIPTION_CHECKOUT_PATH = "/dashboard/subscription/checkout";

const formatPrice = (value, currency = "IDR") => {
  const number = Number(value || 0);

  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(number);
};

const getCycleLabel = (cycle) => {
  if (cycle === "yearly") return "Tahun";
  return "Bulan";
};

const getFeatureBooleanLabel = (value) => {
  if (typeof value === "boolean") return value ? "Ya" : "Tidak";

  const normalized = String(value).toLowerCase();
  if (normalized === "true") return "Ya";
  if (normalized === "false") return "Tidak";

  return value;
};

const getDefaultPrice = (plan, billingCycle) => {
  const prices = Array.isArray(plan?.prices) ? plan.prices : [];
  return (
    prices.find(
      (price) =>
        price.billing_cycle === billingCycle && price.is_active
    ) ||
    prices.find((price) => price.is_default && price.is_active) ||
    prices.find((price) => price.is_active) ||
    null
  );
};

const getPlanBadge = (plan) => {
  if (plan.is_default) return "Default";
  if (plan.is_featured) return "Populer";
  return null;
};

const SubscriptionPlansPage = () => {
  const navigate = useNavigate();
  const { setSidebarCollapsed, setPageTitle } = useDashboard();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [billingCycle, setBillingCycle] = useState("monthly");

  useEffect(() => {
    setPageTitle("Subscription Plans");
    setSidebarCollapsed(true);

    return () => {
      setSidebarCollapsed(false);
    };
  }, [setPageTitle, setSidebarCollapsed]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axiosInstance.get(SUBSCRIPTION_PLANS_ENDPOINT);
        const data = response?.data?.data || [];

        setPlans(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(
          err?.apiMessage || "Gagal memuat paket layanan. Silakan coba lagi."
        );
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, []);

  const visiblePlans = useMemo(() => {
    return [...plans].sort((a, b) => {
      const aOrder = Number(a?.sort_order || 0);
      const bOrder = Number(b?.sort_order || 0);
      return aOrder - bOrder;
    });
  }, [plans]);

  const handleChoosePlan = (plan, selectedPrice) => {
    if (!selectedPrice?.id) {
      setError("Harga paket tidak tersedia.");
      return;
    }

    savePendingSubscriptionSelection({
      planId: plan.id,
      planSlug: plan.slug,
      planName: plan.name,
      billingCycle,
      price: {
        id: selectedPrice.id,
        billing_cycle: selectedPrice.billing_cycle,
        duration_months: selectedPrice.duration_months,
        price: selectedPrice.price,
        currency: selectedPrice.currency,
      },
      plan: {
        id: plan.id,
        slug: plan.slug,
        name: plan.name,
        description: plan.description,
        is_unlimited: plan.is_unlimited,
        property_limit: plan.property_limit,
        featured_property_limit: plan.featured_property_limit,
      },
    });

    if (isAuthenticated()) {
      navigate(SUBSCRIPTION_CHECKOUT_PATH);
      return;
    }

    navigate("/register", {
      state: {
        next: SUBSCRIPTION_CHECKOUT_PATH,
      },
    });
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <MdWorkspacePremium size={18} />
            <span>Paket Layanan</span>
          </div>

          <h1 className={styles.heroTitle}>
            Pilih paket yang paling sesuai untuk bisnis properti Anda.
          </h1>

          <div className={styles.cycleSwitch}>
            <button
              type="button"
              className={`${styles.cycleBtn} ${
                billingCycle === "monthly" ? styles.cycleBtnActive : ""
              }`}
              onClick={() => setBillingCycle("monthly")}
            >
              <MdSwapHoriz size={18} />
              Bulanan
            </button>

            <button
              type="button"
              className={`${styles.cycleBtn} ${
                billingCycle === "yearly" ? styles.cycleBtnActive : ""
              }`}
              onClick={() => setBillingCycle("yearly")}
            >
              <MdTrendingUp size={18} />
              Tahunan
            </button>
          </div>
        </div>
      </section>

      <section className={styles.content}>
        {loading ? (
          <SubscriptionPlansSkeleton />
        ) : error ? (
          <div className={styles.stateBoxError}>
            <MdLockOutline size={24} />
            <p>{error}</p>
          </div>
        ) : visiblePlans.length === 0 ? (
          <div className={styles.stateBox}>
            <p>Tidak ada paket layanan yang tersedia saat ini.</p>
          </div>
        ) : (
          <div className={styles.grid}>
            {visiblePlans.map((plan) => {
              const selectedPrice = getDefaultPrice(plan, billingCycle);
              const badge = getPlanBadge(plan);

              return (
                <article
                  key={plan.id}
                  className={`${styles.card} ${
                    plan.is_featured ? styles.cardFeatured : ""
                  } ${plan.is_default ? styles.cardDefault : ""}`}
                >
                  <div className={styles.cardTop}>
                    <div>
                      <div className={styles.cardHeadingRow}>
                        <h3 className={styles.cardTitle}>{plan.name}</h3>
                        {badge && <span className={styles.badge}>{badge}</span>}
                      </div>

                      <p className={styles.cardDescription}>
                        {plan.description}
                      </p>
                    </div>

                    <div className={styles.priceWrap}>
                      <div className={styles.price}>
                        {selectedPrice
                          ? formatPrice(
                              selectedPrice.price,
                              selectedPrice.currency
                            )
                          : "Hubungi Sales"}
                      </div>
                      <div className={styles.priceMeta}>
                        {selectedPrice ? (
                          <>
                            / {getCycleLabel(selectedPrice.billing_cycle)}
                            {selectedPrice.duration_months === 12
                              ? " (Tahunan)"
                              : " (Bulanan)"}
                          </>
                        ) : (
                          "Harga tidak tersedia"
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles.quotaBox}>
                    <div className={styles.quotaItem}>
                      <span>Listing Properti</span>
                      <strong>
                        {plan.is_unlimited
                          ? "Unlimited"
                          : `${plan.property_limit ?? 0} listing`}
                      </strong>
                    </div>

                    <div className={styles.quotaItem}>
                      <span>Featured Listing</span>
                      <strong>
                        {plan.is_unlimited
                          ? "Unlimited"
                          : `${plan.featured_property_limit ?? 0} item`}
                      </strong>
                    </div>

                    <div className={styles.quotaItem}>
                      <span>Durasi</span>
                      <strong>
                        {plan.duration_type === "yearly"
                          ? "Tahunan"
                          : "Bulanan"}
                      </strong>
                    </div>
                  </div>

                  <div className={styles.featureList}>
                    {Array.isArray(plan.features) &&
                      plan.features
                        .filter((feature) => feature.is_active)
                        .sort(
                          (a, b) =>
                            Number(a.sort_order || 0) - Number(b.sort_order || 0)
                        )
                        .map((feature) => (
                          <div className={styles.featureItem} key={feature.id}>
                            <MdCheckCircle className={styles.featureIcon} size={18} />
                            <div className={styles.featureText}>
                              <span>{feature.feature_label}</span>
                              <strong>
                                {feature.feature_type === "boolean"
                                  ? getFeatureBooleanLabel(feature.feature_value)
                                  : feature.feature_value}
                              </strong>
                            </div>
                          </div>
                        ))}
                  </div>

                  <div className={styles.cardFooter}>
                    <button
                      type="button"
                      className={`${styles.ctaBtn} ${
                        plan.is_featured ? styles.ctaBtnPrimary : ""
                      }`}
                      onClick={() => handleChoosePlan(plan, selectedPrice)}
                    >
                      Pilih Paket
                      <MdArrowForward size={18} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default SubscriptionPlansPage;
